import React from 'react';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import {
  fetchContentPage,
  fetchMemories
} from '../integrations/contentful/queries';
import {
  CmsContentPage,
  CmsMemory,
  instanceOfCmsPopularMemoriesLiftBlock
} from '../integrations/contentful/types';
import Container from '../components/grid/Container';
import MemoryCard from '../components/memories/MemoryCard';
import { t } from '../utils/i18n';
import Spacing from '../components/common/Spacing';
import SEO from '../components/head/SEO';
import { generatePath } from '../urls/generatePath';
import { Paths } from '../urls/Paths';
import { navbarLayout } from '../layouts/navbarLayout';
import H from '../components/elements/H';
import Breadcrumbs, { BreadcrumbItem } from '../components/head/Breadcrumbs';
import { arrayHead } from '../utils/array';
import StickyCol from '../components/grid/StickyCol';
import { columnTopSpacing } from '../styles/theme';
import DisplayOn from '../components/common/DisplayOn';
import Col from '../components/grid/Col';
import Row from '../components/grid/Row';
import LabelledCountRow from '../components/list-pages/LabelledCountRow';
import LabelledCount from '../components/list-pages/LabelledCount';
import ContentBlockList from '../components/content-blocks/ContentBlockList';
import HorizontalLine from '../components/common/HorizontalLine';
import HeadingWithCount from '../components/common/HeadingWithCount';
import TimMessage from '../components/tim/TimMessage';
import { TimPage } from './TimPage';

interface MemoryListPageProps {
  memories: CmsMemory[];
  contentPage?: CmsContentPage;
}

export const memoryListPageBreadcrumbItem: BreadcrumbItem = {
  name: t('Golfmatkat'),
  path: Paths.MemoryList
};

function renderLabelledCounts(
  memoryCount: number,
  destinationCount: number,
  countryCount: number
) {
  return (
    <LabelledCountRow>
      <LabelledCount
        data-testid="memory-count"
        count={memoryCount}
        label={t('golfmatkaa')}
      />
      <LabelledCount
        data-testid="destination-count"
        count={destinationCount}
        label={t('eri kenttää')}
      />
      <LabelledCount
        data-testid="country-count"
        count={countryCount}
        label={t('kaupunkia')}
      />
    </LabelledCountRow>
  );
}

const MEMORY_INTRODUCTION_MESSAGE = t(
  'Golfmatka = matkaajan oma unohtumaton pelireissu. Ja voit varata itsellesi samanlaisen! Eikö olekin mahtavaa? 🤓⛳️ Ole hyvä ja nauti!'
);

const MemoryListPage: TimPage<MemoryListPageProps> = ({
  memories,
  contentPage
}) => {
  if (!contentPage) {
    return null;
  }

  const destinations = uniq(
    flatten(
      memories.map((memory) => {
        return memory.fields.destinations.map(
          (destination) => destination.sys.id
        );
      })
    )
  );
  const countriesCount = uniq(
    flatten(
      memories.map((memory) => {
        return memory.fields.destinations.map((destination) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const country: any = destination.fields.country;
          return typeof country === 'string' ? country : country?.sys?.id;
        });
      })
    ).filter(Boolean)
  ).length;

  // Split Contentful model's content block list to two separate instances to accommodate the layout
  // In future consider creating separate Contenful model for these listing pages instead of using the content page model.
  let popularMemoriesLift;
  let otherContentBlocks;
  if (contentPage.fields.blocks) {
    popularMemoriesLift = contentPage.fields.blocks.find(
      instanceOfCmsPopularMemoriesLiftBlock
    );
    otherContentBlocks = contentPage.fields.blocks.filter(
      (block) => !instanceOfCmsPopularMemoriesLiftBlock(block)
    );
  }

  return (
    <div data-cy="memory-list-page">
      <SEO
        title={t('Golfmatkat')}
        canonicalPath={generatePath(Paths.SearchResults)}
        description={contentPage.fields.seoDescription || t('Golfmatkat')}
      />
      <Breadcrumbs items={[memoryListPageBreadcrumbItem]} />
      <Spacing bottom={4}>{/* TODO: Filtering controls */}</Spacing>
      <Container>
        <Spacing bottom={6}>
          <Row>
            <Col size={[24, 24, 7]}>
              <StickyCol contentTopOffset={columnTopSpacing.sideColumn}>
                <DisplayOn large>
                  <H styling="h2">
                    <strong>{t('Golfmatkat')}</strong>
                  </H>
                  <Spacing top={2.5}>
                    {renderLabelledCounts(
                      memories.length,
                      destinations.length,
                      countriesCount
                    )}
                  </Spacing>
                  <Spacing top={5}>
                    <TimMessage>{MEMORY_INTRODUCTION_MESSAGE}</TimMessage>
                  </Spacing>
                </DisplayOn>
              </StickyCol>
            </Col>
            <Col size={[0, 0, 1]} />
            <Col size={[24, 24, 16]}>
              <Row>
                <Col size={24}>
                  <DisplayOn small medium>
                    <Spacing bottom={4}>
                      {renderLabelledCounts(
                        memories.length,
                        destinations.length,
                        countriesCount
                      )}
                    </Spacing>
                    <Spacing bottom={5}>
                      <TimMessage>{MEMORY_INTRODUCTION_MESSAGE}</TimMessage>
                    </Spacing>
                  </DisplayOn>
                  <Spacing top={columnTopSpacing.contentColumn} bottom={5}>
                    {popularMemoriesLift && (
                      <Spacing bottom={8}>
                        <ContentBlockList blocks={[popularMemoriesLift]} />
                      </Spacing>
                    )}
                    <HeadingWithCount
                      icon={'travel-memory'}
                      iconSize={[2, 3, 3]}
                      text={t('Kaikki golfmatkat')}
                    />
                  </Spacing>
                </Col>
              </Row>
              <Row>
                {memories.map((cmsMemory, index) => {
                  const {
                    mainImage,
                    slug,
                    startDate,
                    endDate,
                    title,
                    subtitle,
                    matkanKesto,
                    golfInfo,
                    tripDepartures,
                    vehicleInfo
                  } = cmsMemory.fields;
                  const traveller = arrayHead(cmsMemory.fields.travellers);

                  if (!traveller) {
                    return null;
                  }

                  return (
                    <Col key={index} size={[24, 12, 12]}>
                      <Spacing bottom={5}>
                        <MemoryCard
                          memory={{
                            mainImage,
                            slug,
                            startDate,
                            endDate,
                            title,
                            subtitle,
                            matkanKesto,
                            golfInfo,
                            tripDepartures,
                            vehicleInfo
                          }}
                          traveller={{
                            name: traveller.fields.title,
                            profilePicture: traveller.fields.profilePicture,
                            slug: traveller.fields.slug
                          }}
                        />
                      </Spacing>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Spacing>
      </Container>
      <HorizontalLine expand={false} />
      <Spacing top={[8, 14, 14]} bottom={14}>
        {otherContentBlocks && <ContentBlockList blocks={otherContentBlocks} />}
      </Spacing>
    </div>
  );
};

MemoryListPage.getLayout = (page) =>
  navbarLayout(
    <DisplayOn small medium>
      <H styling="h4" tag="div">
        <strong>{t('Golfmatkat')}</strong>
      </H>
    </DisplayOn>,
    page
  );

MemoryListPage.getInitialProps = async function () {
  const [contentPage, memories] = await Promise.all([
    fetchContentPage('lomamuistot'),
    fetchMemories()
  ]);
  const httpErrorCode = !contentPage ? 404 : undefined;
  return { contentPage, memories, httpErrorCode };
};

export default MemoryListPage;
